import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import Sidebar from "./components/Sidebar";
import CoachFeedbackWidget from "./components/CoachFeedbackWidget";
import "./App.css";

import {
  CoachLogin,
  Dashboard,
  Clients,
  CoachAdaptations,
  Programs,
  SessionBuilder,
  SessionsLibrary,
  ExercisesLibrary,
  Messages,
  FeedbackInbox,
  ClientDetail,
  WorkGroups,
  Settings,
  ProgrammingAssistantProfile,
  ProgrammingAssistantNew,
  ProgramDraftPreview,
  Tests,
  DiagramsPage,
  LaunchCalendar,
  Subscribe,
  SubscriptionManagement,
  preloadLikelyRoutes,
} from "./routes";
import { warmStartupData } from "./services/prefetch";
import { installApiFetchAuthShim } from "./services/api";
import { CoachAuthProvider, useCoachAuth } from "./context/CoachAuthContext";
import { getDefaultCoachRoute } from "./constants/coachAccess";
import ErrorBoundary from "./components/ErrorBoundary";

const MIN_SPLASH_DURATION_MS = 800;
const THEME_STORAGE_KEY = "oval-coach-theme-mode";

const getSystemTheme = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const readStoredThemeMode = () => {
  if (typeof window === "undefined") return "auto";
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "auto" ? stored : "auto";
};

const resolveTheme = (mode) => (mode === "auto" ? getSystemTheme() : mode);

function AppSplash({ leaving }) {
  return (
    <div className={`app-splash ${leaving ? "is-leaving" : ""}`}>
      <div className="app-splash__card">
        <img
          src="/logo192.png"
          alt="Oval"
          className="app-splash__logo"
          width={84}
          height={84}
          loading="eager"
          decoding="async"
        />
        <div className="app-splash__brand">OVAL</div>
      </div>
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="route-fallback">
      <div className="route-fallback__pulse" />
      <div className="route-fallback__pulse route-fallback__pulse--small" />
      <div className="route-fallback__pulse" />
    </div>
  );
}

function AccessRoute({ feature, adminOnly = false, children }) {
  const { coach, isAdmin, hasFeature } = useCoachAuth();
  const fallbackRoute = getDefaultCoachRoute(coach);

  if (adminOnly && !isAdmin) {
    return <Navigate to={fallbackRoute} replace />;
  }

  if (feature && !hasFeature(feature)) {
    return <Navigate to={fallbackRoute} replace />;
  }

  return children;
}

function AppLayout({ themeMode, resolvedTheme, onThemeModeChange }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  return (
    <div className="app-shell">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <button
        type="button"
        aria-label="Fermer le menu"
        className={`app-sidebar-backdrop ${sidebarOpen ? "is-visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      <main className="app-main">
        <header className="app-mobile-header">
          <button
            type="button"
            className="app-mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
          >
            Menu
          </button>
        </header>

        <div className="app-content">
          <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route
                path="/"
                element={
                  <AccessRoute feature="dashboard">
                    <Dashboard />
                  </AccessRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <AccessRoute feature="clients">
                    <Clients />
                  </AccessRoute>
                }
              />
              <Route
                path="/clients/:id"
                element={
                  <AccessRoute feature="clients">
                    <ClientDetail />
                  </AccessRoute>
                }
              />
              <Route
                path="/adaptations"
                element={
                  <AccessRoute feature="clients">
                    <CoachAdaptations />
                  </AccessRoute>
                }
              />
              <Route
                path="/groups"
                element={
                  <AccessRoute feature="groups">
                    <WorkGroups />
                  </AccessRoute>
                }
              />
              <Route
                path="/programs"
                element={
                  <AccessRoute feature="programs">
                    <Programs />
                  </AccessRoute>
                }
              />
              <Route
                path="/programs/assistant/new"
                element={
                  <AccessRoute feature="programs">
                    <ProgrammingAssistantNew />
                  </AccessRoute>
                }
              />
              <Route
                path="/programs/assistant/drafts/:draftId"
                element={
                  <AccessRoute feature="programs">
                    <ProgramDraftPreview />
                  </AccessRoute>
                }
              />
              <Route
                path="/programs/session-builder"
                element={
                  <AccessRoute feature="programs">
                    <SessionBuilder />
                  </AccessRoute>
                }
              />
              <Route
                path="/sessions"
                element={
                  <AccessRoute feature="programs">
                    <SessionsLibrary />
                  </AccessRoute>
                }
              />
              <Route
                path="/exercises"
                element={
                  <AccessRoute feature="programs">
                    <ExercisesLibrary />
                  </AccessRoute>
                }
              />
              <Route
                path="/sessions/edit/:id"
                element={
                  <AccessRoute feature="programs">
                    <SessionBuilder />
                  </AccessRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <AccessRoute feature="messages">
                    <Messages />
                  </AccessRoute>
                }
              />
              <Route
                path="/feedback"
                element={
                  <AccessRoute adminOnly>
                    <FeedbackInbox />
                  </AccessRoute>
                }
              />
              <Route
                path="/tests"
                element={
                  <AccessRoute feature="tests">
                    <Tests />
                  </AccessRoute>
                }
              />
              <Route
                path="/diagrams"
                element={
                  <AccessRoute feature="programs">
                    <DiagramsPage />
                  </AccessRoute>
                }
              />
              <Route
                path="/launch-calendar"
                element={
                  <AccessRoute adminOnly>
                    <LaunchCalendar />
                  </AccessRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <Settings
                    themeMode={themeMode}
                    resolvedTheme={resolvedTheme}
                    onThemeModeChange={onThemeModeChange}
                  />
                }
              />
              <Route
                path="/settings/assistant"
                element={<ProgrammingAssistantProfile />}
              />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </div>
      </main>
      <CoachFeedbackWidget />
    </div>
  );
}

function ProtectedApp({ themeMode, resolvedTheme, onThemeModeChange }) {
  const location = useLocation();
  const { isAuthenticated, coach } = useCoachAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const defaultRoute = getDefaultCoachRoute(coach);
  if (location.pathname === "/" && defaultRoute !== "/") {
    return <Navigate to={defaultRoute} replace />;
  }

  return (
    <AppLayout
      themeMode={themeMode}
      resolvedTheme={resolvedTheme}
      onThemeModeChange={onThemeModeChange}
    />
  );
}

function AppRouter({ themeMode, resolvedTheme, onThemeModeChange }) {
  const { isAuthenticated, coach } = useCoachAuth();

  useEffect(() => {
    if (coach?.id) {
      Sentry.setUser({ id: coach.id, role: coach.role });
    } else {
      Sentry.setUser(null);
    }
  }, [coach?.id, coach?.role]);

  useEffect(() => {
    if (!isAuthenticated) return;
    preloadLikelyRoutes();
    let idleHandle = null;
    if (typeof window.requestIdleCallback === "function") {
      idleHandle = window.requestIdleCallback(() => warmStartupData());
    } else {
      idleHandle = window.setTimeout(() => warmStartupData(), 60);
    }

    return () => {
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      } else {
        window.clearTimeout(idleHandle);
      }
    };
  }, [isAuthenticated]);

  return (
    <Routes>
      <Route path="/login" element={<CoachLogin />} />
      <Route path="/coach/activate/:token" element={<CoachLogin />} />
      <Route path="/coach/reset-password/:token" element={<CoachLogin />} />
      {/* Public : tunnel d'abonnement web joueur, indépendant de la session coach. */}
      <Route
        path="/subscribe"
        element={
          <Suspense fallback={<RouteFallback />}>
            <Subscribe />
          </Suspense>
        }
      />
      <Route
        path="/subscription"
        element={
          <Suspense fallback={<RouteFallback />}>
            <SubscriptionManagement />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <ProtectedApp
            themeMode={themeMode}
            resolvedTheme={resolvedTheme}
            onThemeModeChange={onThemeModeChange}
          />
        }
      />
    </Routes>
  );
}

export default function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [splashLeaving, setSplashLeaving] = useState(false);
  const [themeMode, setThemeMode] = useState(readStoredThemeMode);
  const [resolvedTheme, setResolvedTheme] = useState(() =>
    resolveTheme(readStoredThemeMode())
  );

  useEffect(() => {
    installApiFetchAuthShim();
    const holdTimer = window.setTimeout(() => {
      setSplashLeaving(true);
      window.setTimeout(() => setShowSplash(false), 220);
    }, MIN_SPLASH_DURATION_MS);
    return () => {
      window.clearTimeout(holdTimer);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    }

    const applyTheme = () => {
      const nextResolvedTheme = resolveTheme(themeMode);
      setResolvedTheme(nextResolvedTheme);
      document.documentElement.dataset.theme = nextResolvedTheme;
      document.documentElement.style.colorScheme = nextResolvedTheme;
    };

    applyTheme();

    if (themeMode !== "auto" || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => applyTheme();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themeMode]);

  return (
    <CoachAuthProvider>
      <BrowserRouter>
        {showSplash && <AppSplash leaving={splashLeaving} />}
        <AppRouter
          themeMode={themeMode}
          resolvedTheme={resolvedTheme}
          onThemeModeChange={setThemeMode}
        />
      </BrowserRouter>
    </CoachAuthProvider>
  );
}
